import React from 'react';
import { DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import {
  Check, ChevronRight, ChevronLeft, Loader2,
  PoundSterling, Ship, Calendar, Wallet, FileText, Plane,
} from 'lucide-react';

const getStepInfo = (step, { isWageAdjustment, isQualifyingHoliday, isContractLinkable, isWorkTrip }) => {
  if (isWageAdjustment) return { title: 'Wage Adjustment Details', icon: Wallet };
  if (isQualifyingHoliday) return { title: 'Qualifying Holiday', icon: Plane };
  if (isContractLinkable) {
    if (isWorkTrip) {
      if (step === 1) return { title: 'Contract', icon: PoundSterling };
      if (step === 2) return { title: 'Trip Details', icon: Ship };
      if (step === 3) return { title: 'Dates', icon: Calendar };
    }
    if (step === 1) return { title: 'Contract', icon: PoundSterling };
    if (step === 2) return { title: 'Details & Dates', icon: Calendar };
  }
  if (step === 1) return { title: 'Trip Details', icon: Ship };
  if (step === 2) return { title: 'Dates', icon: Calendar };
  return { title: 'Details', icon: FileText };
};

export const TripWizardHeader = ({ trip, totalSteps, currentStep, stepFlags }) => (
  <div className="flex items-center justify-between pb-2 border-b border-slate-700">
    <DialogHeader className="p-0 space-y-0">
      <DialogTitle className="text-white text-base">
        {trip ? 'Edit Trip' : 'Add New Trip'}
      </DialogTitle>
    </DialogHeader>

    {totalSteps > 1 && (
      <div className="flex items-center gap-1.5">
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => {
          const stepInfo = getStepInfo(step, stepFlags);
          const StepIcon = stepInfo.icon;
          const isActive = step === currentStep;
          const isCompleted = step < currentStep;

          return (
            <React.Fragment key={step}>
              <div
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md transition-all text-xs font-medium ${
                  isActive
                    ? 'bg-amber-600 text-white'
                    : isCompleted
                      ? 'bg-amber-600/20 text-amber-400'
                      : 'bg-slate-700 text-slate-400'
                }`}
                data-testid={`step-${step}`}
              >
                {isCompleted ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <StepIcon className="h-3.5 w-3.5" />
                )}
                <span className="hidden sm:inline">{stepInfo.title}</span>
              </div>
              {step < totalSteps && (
                <ChevronRight className={`h-3 w-3 ${isCompleted ? 'text-amber-400' : 'text-slate-600'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    )}
  </div>
);

export const TripWizardFooter = ({ trip, currentStep, totalSteps, loading, canGoNext, onPrev, onNext, onClose, onSubmit }) => (
  <div className="flex items-center justify-between w-full">
    <div>
      {currentStep > 1 && (
        <Button type="button" variant="outline" onClick={onPrev}
          className="border-slate-600 text-slate-300" data-testid="prev-step-btn">
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
      )}
    </div>
    <div className="flex items-center gap-2">
      <Button type="button" variant="outline" onClick={onClose} className="border-slate-600 text-slate-300">
        Cancel
      </Button>
      {currentStep < totalSteps ? (
        <Button type="button" onClick={onNext} disabled={!canGoNext}
          className="bg-amber-600 hover:bg-amber-700" data-testid="next-step-btn">
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      ) : (
        <Button type="submit" disabled={loading} onClick={onSubmit}
          className="bg-amber-600 hover:bg-amber-700" data-testid="submit-trip-btn">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {trip ? 'Update Trip' : 'Add Trip'}
        </Button>
      )}
    </div>
  </div>
);
